import { LayoutNode, SectionRect } from '../types';

export interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Resolve a layout AST into section rectangles.
 * H-splits divide height top-to-bottom (first child = top).
 * V-splits divide width left-to-right (first child = left).
 * Uses equal division by default; pass weights for non-uniform splits.
 */
export function resolveLayout(
  node: LayoutNode,
  bounds: Bounds,
  weights?: number[],
): SectionRect[] {
  if (node.kind === 'leaf') {
    return [{
      index: node.index,
      x: bounds.x,
      y: bounds.y,
      width: bounds.width,
      height: bounds.height,
    }];
  }

  const children = node.children;
  const n = children.length;

  // Compute sizes from weights or equal division
  let sizes: number[];
  if (weights && weights.length === n) {
    const total = weights.reduce((a, b) => a + b, 0);
    sizes = weights.map(w => w / total);
  } else {
    sizes = children.map(() => 1 / n);
  }

  const results: SectionRect[] = [];

  if (node.kind === 'hsplit') {
    // H-split: divide height. First child at top (highest Y), last at bottom.
    let currentY = bounds.y + bounds.height;
    for (let i = 0; i < n; i++) {
      const childHeight = bounds.height * sizes[i];
      currentY -= childHeight;
      const childBounds: Bounds = {
        x: bounds.x,
        y: currentY,
        width: bounds.width,
        height: childHeight,
      };
      results.push(...resolveLayout(children[i], childBounds));
    }
  } else {
    // V-split: divide width. First child at left.
    let currentX = bounds.x;
    for (let i = 0; i < n; i++) {
      const childWidth = bounds.width * sizes[i];
      const childBounds: Bounds = {
        x: currentX,
        y: bounds.y,
        width: childWidth,
        height: bounds.height,
      };
      results.push(...resolveLayout(children[i], childBounds));
      currentX += childWidth;
    }
  }

  return results;
}
